<template>
  <div class="FormArticleExport">
    <Row>
      <Column :md="6">
        <Field
          :label="$t('title')"
          :maxlength="255"
          :minlength="1"
          :value.sync="article.title">
          <FieldBtn
            class="light"
            :disabled="!article.topic || article.laptops.length < 1"
            :isLoading="isGeneratingArticleTitle"
            @click="generateTitle">
            {{ $t('generate') }}
          </FieldBtn>
        </Field>
      </Column>
      <Column :md="4">
        <Field
          :label=" $t('slug')"
          :maxlength="100"
          :minlength="1"
          :value="article.slug"
          @update:value="$up('article.slug', $event)" />
        <a
          v-if="article.remoteArticleId"
          class="FieldBtn Btn ib sm with-icon icon-side-left"
          :href="remoteLink"
          rel="noreferrer noopener"
          target="_blank">
          <Icon name="link" />
          {{ $t('open') }}
        </a>
      </Column>
      <Column :md="2">
        <Field
          :label="$t('status')"
          :options="articleStatuses"
          required
          type="select"
          :value.sync="article.status" />
      </Column>
    </Row>
    <FieldMetaDescription :article="article" />
    <Btn
      :disabled="isSavingArticle || !areLaptopImagesSet || !article.siteId"
      icon="wordpress"
      :isLoading="isPublishing"
      @click="isPublished ? update() : publish()">
      {{ isPublished ? $t('update') : $t('publish') }}
    </Btn>
    <Notice v-if="!areLaptopImagesSet">
      {{ $t('missingImage') }}
    </Notice>
  </div>
</template>

<script>
import { actions, getters } from '@utils'
import { map, pipe, prop } from 'rambda'
import FieldMetaDescription from './FieldMetaDescription'
import { pluckId } from '@utils/collections'

export default {
  components: { FieldMetaDescription },
  props: {
    article: Object,
  },
  data() {
    return {
      doShowTools: false,
      isGeneratingArticleTitle: false,
      isPublishing: false,
    }
  },
  computed: {
    areLaptopImagesSet() {
      const laptopIds = pluckId('laptopId', this.article.laptops)

      return this
        .query('laptop', { id: { $in: laptopIds } })
        .every(prop('imageFileId'))
    },
    articleInStore() {
      return this
        .queryOne('article', this.article.id)
    },
    isPublished() {
      return this.article.remoteArticleId
    },
    remoteLink() {
      const { url } = this.queryOne('site', this.article.siteId)

      return [url, this.article.slug]
        .join('/')
    },
    ...getters(
      'articleStatuses',
      'isSavingArticle',
      'site',
    ),
  },
  methods: {
    async generateTitle() {
      const { variants } = await this.$wrap(
        this.CREATE_COMPLETION({
          type: 'GENERATE',
          promptTemplateName: 'ARTICLE_TITLE',
          category: this.article.category,
          articleId: this.article.id,
          fields: {
            topic: this.article.topic,
          },
        }),
        'isGeneratingArticleTitle',
        this.$t('articleTitleGenerated'),
      )
      this.article.title = variants[0].text
    },
    publish() {
      return this.$wrap(
        this.updateArticle,
        'isPublishing',
        this.$t('published'),
      )
    },
    update() {
      return this.$wrap(
        this.updateArticle,
        'isPublishing',
        this.$t('updated'),
      )
    },
    updateArticle() {
      return this.PATCH_ARTICLE({
        ...this.article,
        $doSyncRemote: true,
      })
    },
    ...actions(
      'CREATE_COMPLETION',
      'PATCH_ARTICLE',
    ),
  },
  watch: {
    ...(() => {
      const syncWithStore = pipe(
        map(key => [
          `articleInStore.${key}`,
          {
            immediate: true,
            handler(value) {
              this.$up(`article.${key}`, value)
            },
          },
        ]),
        Object.fromEntries,
      )

      return syncWithStore([
        'description',
        'remoteArticleId',
        'slug',
        'title',
      ])
    })(),
  },
}
</script>

<style lang="scss">
.FormArticleExport {
  padding-bottom: 4rem;
}
</style>

<i18n>
{
  "en": {
    "articleTitleGenerated": "Article title generated",
    "generate": "Generate",
    "missingImage": "Can not publish an article until there are missing laptop images.",
    "open": "Open",
    "publish": "Publish",
    "published": "Article published",
    "slug": "Slug",
    "specTypes": "Specs",
    "status": "Status",
    "title": "Title",
    "update": "Update post",
    "updated": "Post updated"
  }
}
</i18n>
